import { createAjv } from "@jsonforms/core";

export const codeGroupMultipleFileDonwloadJsonSchema = {
  type: "object",
  properties: {
    fileName: {
      type: "string",
      title: "File name (case-sensitive)",
    },
    codeGroupId: {
      type: "string",
      title: "Code Group ID",
    },
    createdBy: {
      type: "string",
      title: "Created By",
    },
    notificationEmailAddress: {
      type: "string",
      title: "Notification Email Address",
    },
    folders: {
      type: "array",
      items: {
        type: "object",
        properties: {
          folder: { type: "string", title: "Folder Name" },
          numOfCodes: { type: "integer", title: "Number of Codes" },
          numOfFiles: { type: "integer", title: "Number of Files" },
        },
        required: ["folder", "numOfCodes", "numOfFiles"],
      },
    },
  },
  required: [
    "fileName",
    "codeGroupId",
    "createdBy",
    "notificationEmailAddress",
    "folders",
  ],
};

export const codeGroupMultipleFileDonwloadUISchema = {
  type: "VerticalLayout",
  elements: [
    {
      type: "VerticalLayout",
      label: "File Name",
      elements: [
        {
          type: "Control",
          scope: "#/properties/fileName",
          label: "File name (case-sensitive)",
        },
      ],
    },
    {
      type: "VerticalLayout",
      label: "Folder Setup",
      elements: [
        {
          type: "Control",
          scope: "#/properties/folders",
          component: "customArray",
          options: {
            detail: {
              type: "HorizontalLayout",
              elements: [
                {
                  type: "Control",
                  scope: "#/properties/folder",
                  label: "Folder Name",
                },
                {
                  type: "Control",
                  scope: "#/properties/numOfCodes",
                  label: "Number of Codes",
                },
                {
                  type: "Control",
                  scope: "#/properties/numOfFiles",
                  label: "Number of Files",
                },
              ],
            },
          },
        },
      ],
    },
  ],
};

// Helpers for Code Group Multi File Download modal
export const isMultiFileFormIncomplete = (formData) => {
  const name = formData?.fileName;
  if (!name || (typeof name === "string" && name.trim() === "")) return true;

  const folders = formData?.folders;
  if (!Array.isArray(folders) || folders.length === 0) return true;

  return folders.some((f) => {
    if (!f) return true;
    const folderName = f.folder;
    const hasFolder =
      !!folderName && (typeof folderName !== "string" || folderName.trim() !== "");
    const hasNumOfCodes = f.numOfCodes !== undefined && f.numOfCodes !== null;
    const hasNumOfFiles = f.numOfFiles !== undefined && f.numOfFiles !== null;
    return !(hasFolder && hasNumOfCodes && hasNumOfFiles);
  });
};

const getFileNameValidationError = (name) => {
  const namePattern = /^[A-Za-z0-9-_ ]+$/;

  if (!name || (typeof name === "string" && name.trim() === "")) {
    return "Folder Label is required.";
  }

  if (typeof name === "string" && !namePattern.test(name)) {
    return "Folder Label must be alphanumeric or contain -,_ only.";
  }

  return null;
};

const getFoldersArrayValidationError = (folders) => {
  if (!Array.isArray(folders) || folders.length === 0) {
    return "At least one folder is required.";
  }

  return null;
};

const getFolderNameValidationError = (folderName, index, seen) => {
  if (!folderName || (typeof folderName === "string" && folderName.trim() === "")) {
    return `File Name is required for folder #${index + 1}.`;
  }

  if (seen.has(folderName)) {
    return "Duplicate Folder Labels are not allowed.";
  }

  seen.add(folderName);
  return null;
};

const getNumOfCodesValidationError = (numOfCodes, index) => {
  const numericValue = Number(numOfCodes);

  if (!Number.isFinite(numericValue)) {
    return `No. of Codes must be numeric for folder #${index + 1}.`;
  }

  if (!(numericValue > 0)) {
    return `No. of Codes must be greater than 0 for folder #${index + 1}.`;
  }

  return null;
};

const getNumOfFilesValidationError = (numOfFiles, numOfCodes, index) => {
  const numericFiles = Number(numOfFiles);

  if (!Number.isFinite(numericFiles)) {
    return `No. of Files must be numeric for folder #${index + 1}.`;
  }

  if (!(numericFiles > 0)) {
    return `No. of Files must be greater than 0 for folder #${index + 1}.`;
  }

  const numericCodes = Number(numOfCodes);
  const maxCodes = numericFiles * 1000000000;

  if (!(numericCodes <= maxCodes)) {
    return `No. of Codes must be less than or equal to ${maxCodes} for folder #${index + 1}.`;
  }

  return null;
};

const getFolderValidationError = (folder, index, seen) => {
  if (!folder) {
    return `Folder #${index + 1} is incomplete.`;
  }

  const folderName = folder.folder;
  const nameError = getFolderNameValidationError(folderName, index, seen);
  if (nameError) {
    return nameError;
  }

  const codesError = getNumOfCodesValidationError(folder.numOfCodes, index);
  if (codesError) {
    return codesError;
  }

  const filesError = getNumOfFilesValidationError(
    folder.numOfFiles,
    folder.numOfCodes,
    index
  );
  if (filesError) {
    return filesError;
  }

  return null;
};

export const extraValidateMultiFile = (formData) => {
  const name = formData?.fileName;
  const nameError = getFileNameValidationError(name);
  if (nameError) {
    return { valid: false, message: nameError };
  }

  const folders = formData?.folders;
  const foldersArrayError = getFoldersArrayValidationError(folders);
  if (foldersArrayError) {
    return { valid: false, message: foldersArrayError };
  }

  const seen = new Set();
  for (let i = 0; i < folders.length; i++) {
    const folderError = getFolderValidationError(folders[i], i, seen);
    if (folderError) {
      return { valid: false, message: folderError };
    }
  }

  return { valid: true };
};

export const validateMultiFileSchema = (payload) => {
  const ajv = createAjv();
  const validate = ajv.compile(codeGroupMultipleFileDonwloadJsonSchema);
  const valid = validate(payload);
  return { valid, errors: validate.errors };
};
